import { QueryClient } from '@tanstack/react-query';
import apiClient from './api';
import { LinkedPatient } from '../hooks/api/useLinkedPatients';

const STORAGE_KEY = 'onboarding';

export interface OnboardingData {
  token: string;
  patientId: number;
  patientName: string;
  officeId: string;
  officeName: string;
  companyName: string;
  relationship: string;
  lineUserId: string;
}

export function saveOnboardingData(data: OnboardingData): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (err) {
    console.error('オンボーディングデータの保存に失敗しました:', err);
    throw new Error('ブラウザのストレージへの保存に失敗しました。プライベートブラウズモードでは利用できない場合があります。');
  }
}

export function getOnboardingData(): OnboardingData | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as OnboardingData;
  } catch {
    return null;
  }
}

export function clearOnboardingData(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

/**
 * 家族連携を確定し、キャッシュを更新してsessionStorageをクリアする。
 * LinkToken / ConsentPage / UserProfilePage の3箇所から共通で呼ばれる。
 *
 * 返り値: 新たに連携した利用者の patient_id。
 * 呼び出し元はこの値を `?patient_id=` クエリに乗せてホームへ遷移することで、
 * 2人目以降の登録時にホーム画面の着地先が新規利用者になるようにする。
 */
export async function confirmAndLinkFamily(
  queryClient: QueryClient,
): Promise<number> {
  const data = getOnboardingData();
  if (!data) {
    throw new Error('オンボーディングデータが見つかりません。招待リンクからやり直してください。');
  }

  try {
    const linkResult = await apiClient.confirmFamilyLink({
      token: data.token,
      line_user_id: data.lineUserId,
      relationship: data.relationship,
    });

    if (!linkResult.patient_id) {
      throw new Error(linkResult.message || '連携処理に失敗しました');
    }

    // DynamoDB結果整合性対策: キャッシュを直接更新
    if (linkResult.patient) {
      const { patient } = linkResult;
      const existingData = queryClient.getQueryData<LinkedPatient[]>(['linkedPatients']);
      const newPatient: LinkedPatient = {
        patient_id: patient.patient_id,
        patient_name: patient.patient_name,
        role: patient.role,
        office_id: patient.office_id,
        office_name: patient.office_name,
        relationship: data.relationship,
      };

      // 本人連携は UserProfile 画面で birth_date/gender を読み取り専用表示するが、
      // /api/link/confirm のレスポンス（無認証エンドポイント）には APPI 配慮で
      // birth_date/gender が含まれない。partial 追記するとそれらを欠いた本人レコードで
      // キャッシュが汚染され「未登録」表示になるため、本人連携時は追記せず取り直す。
      const isSelfLink = data.relationship === '本人';

      if (existingData && !isSelfLink) {
        // 本人以外かつ既存キャッシュがある場合のみ、新規連携を追記して即時反映する。
        // 既に追加済みの場合は重複させない。
        const exists = existingData.some((p) => p.patient_id === newPatient.patient_id);
        if (!exists) {
          queryClient.setQueryData<LinkedPatient[]>(['linkedPatients'], [...existingData, newPatient]);
        }
      } else {
        // 以下のいずれかのケース。部分キャッシュを置かず次の購読でサーバから全件取り直させる:
        // - オンボーディング中は PatientProvider が未マウントのため existingData は undefined。
        //   `[newPatient]` を入れると次画面（PatientGate）が isLoading=false の部分キャッシュで
        //   描画され、既存連携が一瞬抜け落ちて見える。
        // - 本人連携は birth_date/gender を欠いた newPatient での汚染を避ける。
        queryClient.removeQueries({ queryKey: ['linkedPatients'], exact: true });
      }

      // 既存キャッシュを更新したケースでもサーバの真値と差異が出ないよう invalidate。
      // remove したケースでは、次の購読時に refetch が走る。
      queryClient.invalidateQueries({ queryKey: ['linkedPatients'] });
    }

    clearOnboardingData();
    return linkResult.patient_id;
  } catch (err: any) {
    // 回復不能エラー（HTTP 400: トークン期限切れ・使用済み）の場合のみクリア
    // 一時的エラー（ネットワーク障害・500系）の場合はデータを保持してリトライ可能にする
    const statusCode = err?.statusCode ?? err?.status;
    if (statusCode === 400) {
      clearOnboardingData();
    }
    throw err;
  }
}
